import { Carousel, Modal, Spin, Tabs, message } from 'antd';
import type { CarouselRef } from 'antd/es/carousel';
import { CloseOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faChevronLeft, faChevronRight, faDownload, faNoteSticky } from '@fortawesome/free-solid-svg-icons';
import uspsSvg from './tracking/usps.svg';
import fedexGif from './tracking/fedex_logo_small.gif';
import dhlGif from './tracking/dhl_logo.gif';
import { ordersService } from '../../../services';
import { downloadDataAsCSV } from '../../../utils/downloadFile';
import { ItemDetailsResponse, RecipientRow, TrackingData } from './types';
import OverviewTab from './tabs/OverviewTab';
import ItemDetailsTab from './tabs/ItemDetailsTab';
import ApiTraceTab from './tabs/ApiTraceTab';
import MailingTrackingView, { formatMailDate } from './tracking/MailingTrackingView';
import ShippingTrackingView from './tracking/ShippingTrackingView';
import styles from './item-details.module.scss';

type ActiveView = 'main' | 'mailing-tracking' | 'shipping-tracking';

interface Props {
  open: boolean;
  itemId: string | null;
  onClose: () => void;
}

const ItemDetailsModal: React.FC<Props> = ({ open, itemId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ItemDetailsResponse | null>(null);
  const [activeView, setActiveView] = useState<ActiveView>('main');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mailingData, setMailingData] = useState<TrackingData | null>(null);
  const [mailingLoading, setMailingLoading] = useState(false);
  const [mailingError, setMailingError] = useState<string | null>(null);
  const [shippingData, setShippingData] = useState<any>(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const carouselRef = useRef<CarouselRef>(null);

  useEffect(() => {
    if (!open || !itemId) return;
    setActiveView('main');
    setShippingData(null);
    setMailingData(null);
    setMailingError(null);
    setLoading(true);
    ordersService
      .getItemDetails(itemId)
      .then((res: ItemDetailsResponse) => setData(res))
      .catch((e: any) => message.error(e?.message || 'Failed to load item details'))
      .finally(() => setLoading(false));
  }, [open, itemId]);

  useEffect(() => {
    if (activeView !== 'shipping-tracking' || shippingData) return;
    const shipment = data?.overview?.shipping?.shipments?.[0];
    if (!shipment?.carrierName || !shipment?.trackingNumber) return;
    setShippingLoading(true);
    ordersService
      .getShippingTrackingData(shipment.carrierName, shipment.trackingNumber)
      .then((res: any) => setShippingData(res))
      .catch(() => {})
      .finally(() => setShippingLoading(false));
  }, [activeView, data, shippingData]);

  useEffect(() => {
    if (activeView !== 'mailing-tracking' || mailingData || !itemId) return;
    setMailingLoading(true);
    setMailingError(null);
    ordersService
      .getTrackingData(itemId)
      .then((res: TrackingData) => setMailingData(res))
      .catch((e: any) => setMailingError(e?.message || 'Mailing info unavailable. Please try again later.'))
      .finally(() => setMailingLoading(false));
  }, [activeView, itemId, mailingData]);

  const formatMailDateTime = (value: string | null): string => {
    const { date, time } = formatMailDate(value);
    return date ? `${date} ${time ?? ''}`.trim() : '';
  };

  const handleMailingDownload = () => {
    const recipients = mailingData?.recipients ?? [];
    if (!recipients.length) return;
    const rows = recipients.map((r: RecipientRow) => ({
      'RECIPIENT NAME':          r.recipientName,
      'RECIPIENT ADDRESS':       r.recipientAddress,
      'QR SCAN':                 formatMailDateTime(r.qrScan),
      'ID CLICK TIME':           formatMailDateTime(r.idClickTime),
      'STATUS':                  r.trackingStatus ?? 'In Processing',
      'LAST UPDATE':             formatMailDateTime(r.lastUpdate),
      'INTELLIGENT MAIL BARCODE': r.intelligentMailBarcode,
    }));
    downloadDataAsCSV(rows, `Scan_data_status_${itemId}_${Date.now()}.csv`);
  };

  const handleShippingDownload = () => {
    const timeline = shippingData?.timeline ?? [];
    if (!timeline.length) return;
    const rows = timeline.map((row: any) => ({
      'Date/Time': new Date(row.dateTime).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
      'Activity':  row.event,
      'Location':  row.location,
    }));
    downloadDataAsCSV(rows, `Shipping_tracking_${itemId}_${Date.now()}.csv`);
  };

  const isMailing = data
    ? data.overview.mailingType !== 'shipped'
    : true;

  const tabs = isMailing
    ? [
        {
          key: 'overview',
          label: 'Overview',
          children: (
            <OverviewTab
              data={data}
              isMailing
              onViewMailingTracking={() => setActiveView('mailing-tracking')}
            />
          ),
        },
        {
          key: 'item-details',
          label: 'Item Details',
          children: <ItemDetailsTab data={data} />,
        },
        {
          key: 'api-trace',
          label: 'API Trace',
          children: <ApiTraceTab data={data} />,
        },
      ]
    : [
        {
          key: 'overview',
          label: 'Overview',
          children: (
            <OverviewTab
              data={data}
              isMailing={false}
              onViewShippingTracking={() => setActiveView('shipping-tracking')}
            />
          ),
        },
        {
          key: 'item-details',
          label: 'Item Details',
          children: <ItemDetailsTab data={data} />,
        },
        {
          key: 'api-trace',
          label: 'API Trace',
          children: <ApiTraceTab data={data} />,
        },
      ];

  const handleClose = () => {
    setData(null);
    setActiveView('main');
    onClose();
  };

  const overview = data?.overview;
  const tmpl = data?.itemDetails?.template;

  const isShipping = activeView === 'shipping-tracking';
  const shipment = overview?.shipping?.shipments?.[0];
  const carrier = (shipment?.carrierName ?? '').toUpperCase();
  const trackingNumber = shipment?.trackingNumber ?? '';
  const carrierTrackingUrl = carrier === 'DHL'
    ? `https://www.dhl.com/en/express/tracking.html?AWB=${trackingNumber}`
    : `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
  const carrierLogo = carrier === 'DHL' ? dhlGif : fedexGif;

  const hasMailingData = !!mailingData?.recipients?.length;
  const hasShippingData = !!shippingData?.timeline?.length;

  const modalTitle =
    activeView === 'main' ? (
      <div className={styles.modalHeader}>
        <span className={styles.headerLabel}>ITEM REF ID</span>
        <span className={styles.headerValue}>{overview?.itemReferenceId ?? '–'}</span>
        <div className={styles.modalHeaderSpacer} />
        <button className={styles.modalCloseButton} onClick={handleClose}>
          <CloseOutlined />
        </button>
      </div>
    ) : (
      <div className={styles.trackingHeader}>
        <button className={styles.backButton} onClick={() => setActiveView('main')}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <div className={styles.trackingHeaderText}>
          <span className={styles.headerLabel}>ITEM REF ID</span>
          <span className={styles.trackingHeaderId}>{overview?.itemReferenceId ?? '–'}</span>
          <span className={styles.trackingHeaderTitle}>
            {isShipping ? 'Shipping Status Tracking' : 'Mail Status Tracking'}
          </span>
          {isShipping ? (
            <>
              {trackingNumber && (
                <a
                  href={carrierTrackingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.trackingNumberHeaderLink}
                >
                  Tracking# {trackingNumber}
                </a>
              )}
              <button
                className={styles.downloadButton}
                onClick={handleShippingDownload}
                disabled={!hasShippingData}
              >
                <FontAwesomeIcon icon={faDownload} />
              </button>
            </>
          ) : (
            <button
              className={styles.downloadButton}
              onClick={handleMailingDownload}
              disabled={!hasMailingData}
            >
              <FontAwesomeIcon icon={faDownload} />
            </button>
          )}
        </div>
        <div className={styles.trackingHeaderActions}>
          {isShipping ? (
            <img src={carrierLogo as string} alt={carrier || 'Carrier'} className={styles.uspsLogo} />
          ) : (
            <img src={uspsSvg as string} alt="USPS" className={styles.uspsLogo} />
          )}
          <button className={styles.modalCloseButton} onClick={handleClose}>
            <CloseOutlined />
          </button>
        </div>
      </div>
    );

  const previewImages = tmpl?.previewImages;
  const carouselSlides = [previewImages?.front, previewImages?.back].filter(Boolean) as string[];
  if (carouselSlides.length === 0) carouselSlides.push('', '');

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      width={1000}
      height={550}
      centered
      destroyOnHidden
      footer={null}
      closable={false}
      title={modalTitle}
      className={styles.itemDetailsModal}
      wrapClassName={styles.itemDetailsModalWrap}
      styles={{ header: { borderBottom: 'none', padding: 0, marginBottom: 0 } }}
    >
      <Spin spinning={loading}>
        {activeView === 'main' && (
          <div className={styles.modalBody}>
            <div className={styles.leftPanel}>
              <div className={styles.proofContainer}>
                <div className={styles.carouselWrapper}>
                  <Carousel ref={carouselRef} dots={false} beforeChange={(_, next) => setCurrentSlide(next)}>
                    {carouselSlides.map((img, i) => (
                      <div key={i}>
                        <div className={styles.carouselSlide}>
                          {img ? (
                            <img src={img} alt={`Item preview ${i + 1}`} />
                          ) : (
                            <div className={styles.imagePlaceholder} />
                          )}
                        </div>
                      </div>
                    ))}
                  </Carousel>
                  {carouselSlides.length > 1 && (
                    <>
                      <button
                        className={`${styles.carouselNavButton} ${styles.carouselNavButtonLeft}`}
                        onClick={() => carouselRef.current?.prev()}
                        aria-label="Previous image"
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </button>
                      <button
                        className={`${styles.carouselNavButton} ${styles.carouselNavButtonRight}`}
                        onClick={() => carouselRef.current?.next()}
                        aria-label="Next image"
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </>
                  )}
                </div>
                <div className={styles.customDots}>
                  {carouselSlides.map((_, i) => (
                    <div
                      key={i}
                      className={`${styles.slickDot} ${i === currentSlide ? styles.slickDotActive : ''}`}
                      onClick={() => carouselRef.current?.goTo(i)}
                    />
                  ))}
                </div>
              </div>
              <div className={styles.templateCard}>
                <div className={styles.templateIconWrapper}>
                  <FontAwesomeIcon icon={faNoteSticky} className={styles.templateIcon} />
                </div>
                <div className={styles.templateInfo}>
                  <div className={styles.templateLabel}>TEMPLATE</div>
                  <div className={styles.templateName}>{tmpl?.name ?? '–'}</div>
                </div>
              </div>
            </div>

            <div className={styles.rightPanel}>
              <Tabs items={tabs} />
            </div>
          </div>
        )}

        {activeView === 'mailing-tracking' && (
          <MailingTrackingView
            data={mailingData}
            loading={mailingLoading}
            error={mailingError}
            totalPiecesOrdered={overview?.quantity?.total}
            qrCodesScanned={overview?.tracking?.qrCodesScanned}
            informedDeliveryClicks={overview?.tracking?.informedDeliveryClicks}
          />
        )}

        {activeView === 'shipping-tracking' && (
          <ShippingTrackingView data={shippingData} loading={shippingLoading} />
        )}
      </Spin>
    </Modal>
  );
};

export default ItemDetailsModal;
